import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Search,
  Lock,
  Unlock,
  AlertTriangle,
  FileX,
  CreditCard,
  Ban,
  CheckCircle,
  Loader2,
  Clock,
  History,
  Filter,
  Eye,
  MessageSquare,
  Info,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status definitions
const STATUSES = {
  actif: { label: "Actif", color: "green", icon: CheckCircle },
  en_attente_validation: { label: "En attente", color: "amber", icon: Clock },
  bloque_documents: { label: "Bloqué (Documents)", color: "red", icon: FileX },
  bloque_paiement: { label: "Bloqué (Paiement)", color: "red", icon: CreditCard },
  inactif: { label: "Inactif", color: "gray", icon: Ban },
};

const BLOCK_REASONS = [
  { value: "documents_manquants", label: "Documents manquants" },
  { value: "documents_expires", label: "Documents expirés" },
  { value: "assurance_invalide", label: "Assurance invalide" },
  { value: "cotisation_impayee", label: "Cotisation impayée" },
  { value: "paiement_en_retard", label: "Paiement en retard" },
  { value: "comportement", label: "Problème de comportement" },
  { value: "inactivite", label: "Inactivité prolongée" },
  { value: "demande_membre", label: "Demande du membre" },
  { value: "autre", label: "Autre raison" },
];

export default function BlockagesPage() {
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Block dialog
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedFoodTruck, setSelectedFoodTruck] = useState(null);
  const [blockType, setBlockType] = useState("bloque_documents");
  const [blockReason, setBlockReason] = useState("");
  const [blockNote, setBlockNote] = useState("");
  const [blocking, setBlocking] = useState(false);
  
  // Unblock dialog
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  
  // History dialog
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [blockHistory, setBlockHistory] = useState([]);
  
  // Store last block reason for each blocked food truck
  const [blockReasons, setBlockReasons] = useState({});

  useEffect(() => {
    loadFoodTrucks();
  }, []);

  const loadFoodTrucks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/food-trucks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFoodTrucks(response.data);
      
      // Load block reasons for blocked food trucks
      const blockedFts = response.data.filter(ft => 
        ft.statut === "bloque_documents" || ft.statut === "bloque_paiement"
      );
      
      if (blockedFts.length > 0) {
        const reasonsMap = {};
        await Promise.all(blockedFts.map(async (ft) => {
          try {
            const historyRes = await axios.get(`${API_URL}/api/blockages/history/${ft.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            // Get the most recent block action
            const lastBlock = historyRes.data.find(entry => entry.action === "block");
            if (lastBlock) {
              reasonsMap[ft.id] = {
                reason: lastBlock.reason,
                note: lastBlock.note,
                block_type: lastBlock.block_type,
                date: lastBlock.created_at,
                admin_name: lastBlock.admin_name
              };
            }
          } catch (err) {
            // Ignore errors for individual history fetches
          }
        }));
        setBlockReasons(reasonsMap);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const openBlockDialog = (ft) => {
    setSelectedFoodTruck(ft);
    setBlockType("bloque_documents");
    setBlockReason("");
    setBlockNote("");
    setShowBlockDialog(true);
  };

  const openUnblockDialog = (ft) => {
    setSelectedFoodTruck(ft);
    setShowUnblockDialog(true);
  };

  const handleBlock = async () => {
    if (!blockReason) {
      toast.error("Veuillez sélectionner une raison");
      return;
    }

    setBlocking(true);
    try {
      const token = localStorage.getItem("token");
      
      // Update food truck status
      await axios.put(`${API_URL}/api/food-trucks/${selectedFoodTruck.id}`, {
        statut: blockType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log the block action
      await axios.post(`${API_URL}/api/blockages/log`, {
        food_truck_id: selectedFoodTruck.id,
        action: "block",
        block_type: blockType,
        reason: blockReason,
        note: blockNote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${selectedFoodTruck.nom_food_truck} a été bloqué`);
      setShowBlockDialog(false);
      loadFoodTrucks();
    } catch (error) {
      toast.error("Erreur lors du blocage");
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    setUnblocking(true);
    try {
      const token = localStorage.getItem("token");
      
      // Update food truck status
      await axios.put(`${API_URL}/api/food-trucks/${selectedFoodTruck.id}`, {
        statut: "actif"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log the unblock action
      await axios.post(`${API_URL}/api/blockages/log`, {
        food_truck_id: selectedFoodTruck.id,
        action: "unblock",
        block_type: null,
        reason: "deblocage_manuel",
        note: ""
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${selectedFoodTruck.nom_food_truck} a été débloqué`);
      setShowUnblockDialog(false);
      loadFoodTrucks();
    } catch (error) {
      toast.error("Erreur lors du déblocage");
    } finally {
      setUnblocking(false);
    }
  };

  const loadHistory = async (ft) => {
    setSelectedFoodTruck(ft);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/blockages/history/${ft.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockHistory(response.data);
      setShowHistoryDialog(true);
    } catch (error) {
      // If endpoint doesn't exist yet, show empty history
      setBlockHistory([]);
      setShowHistoryDialog(true);
    }
  };

  const getStatusBadge = (statut) => {
    const status = STATUSES[statut] || STATUSES.actif;
    const Icon = status.icon;
    return (
      <Badge 
        variant="outline" 
        className={`bg-${status.color}-100 text-${status.color}-800 border-${status.color}-300`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status.label}
      </Badge>
    );
  };

  // Helper to get readable reason label
  const getReasonLabel = (reasonValue) => {
    const reasonObj = BLOCK_REASONS.find(r => r.value === reasonValue);
    return reasonObj ? reasonObj.label : reasonValue;
  };

  // Render block reason for a food truck
  const renderBlockReason = (ftId) => {
    const blockInfo = blockReasons[ftId];
    if (!blockInfo) return <span className="text-muted-foreground text-sm italic">Non renseignée</span>;
    
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {getReasonLabel(blockInfo.reason)}
          </Badge>
        </div>
        {blockInfo.note && (
          <p className="text-xs text-muted-foreground line-clamp-2" title={blockInfo.note}>
            <MessageSquare className="w-3 h-3 inline mr-1" />
            {blockInfo.note}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {blockInfo.date && new Date(blockInfo.date).toLocaleDateString("fr-FR")}
          {blockInfo.admin_name && ` par ${blockInfo.admin_name}`}
        </p>
      </div>
    );
  };

  const filteredFoodTrucks = foodTrucks.filter(ft => {
    const matchesSearch = 
      ft.nom_food_truck?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ft.nom_contact?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ft.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === "all" || ft.statut === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Group by status
  const blockedFoodTrucks = filteredFoodTrucks.filter(ft => 
    ft.statut === "bloque_documents" || ft.statut === "bloque_paiement"
  );
  const activeFoodTrucks = filteredFoodTrucks.filter(ft => ft.statut === "actif");
  const pendingFoodTrucks = filteredFoodTrucks.filter(ft => ft.statut === "en_attente_validation");
  const inactiveFoodTrucks = filteredFoodTrucks.filter(ft => ft.statut === "inactif");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-secondary flex items-center gap-3">
          <Lock className="w-8 h-8 text-primary" />
          Gestion des Blocages
        </h1>
        <p className="text-muted-foreground mt-1">
          Gérez les blocages et restrictions des comptes food trucks
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setFilterStatus("all")}>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{foodTrucks.length}</div>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-green-50 border-green-200" onClick={() => setFilterStatus("actif")}>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{activeFoodTrucks.length}</div>
            <p className="text-sm text-muted-foreground">Actifs</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-red-50 border-red-200" onClick={() => setFilterStatus("bloque_documents")}>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">
              {foodTrucks.filter(ft => ft.statut === "bloque_documents").length}
            </div>
            <p className="text-sm text-muted-foreground">Bloqués (Docs)</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-red-50 border-red-200" onClick={() => setFilterStatus("bloque_paiement")}>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">
              {foodTrucks.filter(ft => ft.statut === "bloque_paiement").length}
            </div>
            <p className="text-sm text-muted-foreground">Bloqués (Paiement)</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:bg-amber-50 border-amber-200" onClick={() => setFilterStatus("en_attente_validation")}>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-amber-600">{pendingFoodTrucks.length}</div>
            <p className="text-sm text-muted-foreground">En attente</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Rechercher un food truck..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="bloque_documents">Bloqué (Documents)</SelectItem>
                <SelectItem value="bloque_paiement">Bloqué (Paiement)</SelectItem>
                <SelectItem value="en_attente_validation">En attente</SelectItem>
                <SelectItem value="inactif">Inactif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Blocked Food Trucks */}
      {blockedFoodTrucks.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Comptes Bloqués ({blockedFoodTrucks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Food Truck</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Raison du blocage</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedFoodTrucks.map((ft) => (
                  <TableRow key={ft.id} className="bg-red-50/50">
                    <TableCell className="font-medium">{ft.nom_food_truck}</TableCell>
                    <TableCell>{ft.nom_contact || "-"}</TableCell>
                    <TableCell>{getStatusBadge(ft.statut)}</TableCell>
                    <TableCell className="max-w-[250px]">
                      {renderBlockReason(ft.id)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => loadHistory(ft)}
                          title="Voir l'historique complet"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="default" 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openUnblockDialog(ft)}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          Débloquer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Active Food Trucks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Comptes Actifs ({activeFoodTrucks.length})
          </CardTitle>
          <CardDescription>
            Cliquez sur "Bloquer" pour restreindre l'accès d'un compte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Food Truck</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeFoodTrucks.map((ft) => (
                <TableRow key={ft.id}>
                  <TableCell className="font-medium">{ft.nom_food_truck}</TableCell>
                  <TableCell>{ft.nom_contact || "-"}</TableCell>
                  <TableCell className="text-muted-foreground">{ft.email}</TableCell>
                  <TableCell className="text-muted-foreground">{ft.telephone || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => loadHistory(ft)}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => openBlockDialog(ft)}
                      >
                        <Lock className="w-4 h-4 mr-1" />
                        Bloquer
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Block Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Lock className="w-5 h-5" />
              Bloquer un compte
            </DialogTitle>
            <DialogDescription>
              Bloquer "{selectedFoodTruck?.nom_food_truck}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Type de blocage</Label>
              <Select value={blockType} onValueChange={setBlockType}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bloque_documents">
                    <span className="flex items-center gap-2">
                      <FileX className="w-4 h-4 text-red-600" />
                      Blocage Documents
                    </span>
                  </SelectItem>
                  <SelectItem value="bloque_paiement">
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-red-600" />
                      Blocage Paiement
                    </span>
                  </SelectItem>
                  <SelectItem value="inactif">
                    <span className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-gray-600" />
                      Inactif
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Raison du blocage</Label>
              <Select value={blockReason} onValueChange={setBlockReason}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Sélectionner une raison" />
                </SelectTrigger>
                <SelectContent>
                  {BLOCK_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Note (optionnel)</Label>
              <Textarea
                value={blockNote}
                onChange={(e) => setBlockNote(e.target.value)}
                placeholder="Ajouter des détails supplémentaires..."
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBlock}
              disabled={blocking}
            >
              {blocking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Bloquer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock Dialog */}
      <AlertDialog open={showUnblockDialog} onOpenChange={setShowUnblockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-700">
              <Unlock className="w-5 h-5" />
              Débloquer le compte
            </AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir débloquer "{selectedFoodTruck?.nom_food_truck}" ?
              <br /><br />
              Le compte retrouvera un accès complet à l'application.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnblock}
              disabled={unblocking}
              className="bg-green-600 hover:bg-green-700"
            >
              {unblocking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Unlock className="w-4 h-4 mr-2" />
              )}
              Débloquer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique - {selectedFoodTruck?.nom_food_truck}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {blockHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun historique de blocage
              </p>
            ) : (
              <div className="space-y-3">
                {blockHistory.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    {entry.action === "block" ? (
                      <Lock className="w-5 h-5 text-red-600 mt-0.5" />
                    ) : (
                      <Unlock className="w-5 h-5 text-green-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-medium">
                          {entry.action === "block" ? "Blocage" : "Déblocage"}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {new Date(entry.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </div>
                      {entry.reason && (
                        <p className="text-sm text-muted-foreground">
                          Raison: {BLOCK_REASONS.find(r => r.value === entry.reason)?.label || entry.reason}
                        </p>
                      )}
                      {entry.note && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Note: {entry.note}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
